import {Component, ElementRef, Input, Output, EventEmitter, OnInit, ViewChild, Renderer2} from '@angular/core';
import * as d3 from 'd3';
import {Globals} from '../globals';

@Component({
  selector: 'app-d3circles',
  templateUrl: './d3circles.component.html',
  styleUrls: ['./d3circles.component.css']
})
export class D3circlesComponent implements OnInit {
  @Input() params: string = '';
  @Input() token: string = '';
  @Input() data:any = null;
  @Output() dataEvent = new EventEmitter<any>();
  @ViewChild('view') panel: ElementRef<HTMLDivElement> | undefined;

  loading: boolean = false;
  title: string = '';
  subtitle: string = '';

  constructor(private renderer: Renderer2, private globals: Globals) { }

  ngOnInit(): void {
    if(this.data == null) this.showD3(this.params);
    else setTimeout(()=>this.drawChart(this, this.data), 100); // need time to init the panel
  }

  showD3(params: string): void {
    let me = this;
    d3.select('#d3circles').selectAll("*").remove();
    me.loading = true;
    let data = d3.json(this.globals.url_root + "/Query.action?sql=SQL_JSON_AIRPORT_CHECKPOINT"+params, {
      headers: new Headers({
        "Authorization": "Bearer " + this.token
      })
    });
    data.then(function(data) {
      me.loading = false;
      me.data = data;
      me.dataEvent.emit(data);
      me.drawChart(me, data);
    });
  };

  /**
   * Refresh the drawing
   * @param me: Calling instance
   * @param data: content
   */
  drawChart(me: any, data: any) {
    let width = me.panel.nativeElement.offsetWidth;
    let height = me.panel.nativeElement.offsetHeight;
    me.title = "Traffic Distribution by Airport";
    me.subtitle = "Click to zoom in";

    let color = d3.scaleLinear<string>()
      .domain([0, 5])
      .range(["hsl(152,80%,80%)", "hsl(228,30%,40%)"])
      .interpolate(d3.interpolateHcl);

    let pack = (data: any) => d3.pack()
      .size([width, height])
      .padding(3)
      (d3.hierarchy(data)
        .sum(d => d.value)
        .sort((a: any, b: any) => b.value - a.value))

    const root = pack(data);
    let focus = root;
    let view: any;

    function zoom(event: any, d:any) {
      focus = d;

      const transition: any = svg.transition()
        .duration(750)
        .tween("zoom", () => {
          const i = d3.interpolateZoom(view, [focus.x, focus.y, focus.r * 2]);
          return (t:any) => zoomTo(i(t));
        });

      label
        .filter(function(d) { return d.parent === focus || (<HTMLElement>this).style.display === "inline"; })
        .transition(transition)
        .style("fill-opacity", d => d.parent === focus ? 0.6 : 0)
        .style("display", d => (d.parent === root && d.r < 8) ? 'none': 'inline')
        .on("start", function(d) { if (d.parent === focus) (<HTMLElement>this).style.display = "inline"; })
        .on("end", function(d) { if (d.parent !== focus) (<HTMLElement>this).style.display = "none"; });

      if(d.data.name == "data") {
        me.title = "Traffic Distribution by Airport";
        me.subtitle = "Click to zoom in";
      } else {
        me.title = "Traffic Break-down at " + d.data.name;
        me.subtitle = "Click to zoom out";
      }

      return svg.node();
    }

    d3.select('#d3circles').selectAll("*").remove();

    const svg = d3.select('#d3circles').append("svg")
      .attr("viewBox", `-${width / 2} -${height / 2} ${width} ${height}`)
      .style("display", "block")
      .style("margin", "5px 5px")
      .style("background", "white")
      .style("cursor", "pointer")
      .on("click", (event: any) => zoom(event, root));

    const node = svg.append("g")
      .selectAll("circle")
      .data(root.descendants().slice(1))
      .join("circle")
      .attr("fill", d => d.children ? color(d.depth) : "white")
      .attr("pointer-events", d => !d.children ? "none" : null)
      .on("mouseover", function() { d3.select(this).attr("stroke", "#000"); })
      .on("mouseout", function() { d3.select(this).attr("stroke", null); })
      .on("click", (event, d) => focus !== d && (zoom(event, d), event.stopPropagation()));

    const label = svg.append("g")
      .style("font", "12px sans-serif")
      .attr("pointer-events", "none")
      .attr("text-anchor", "middle")
      .selectAll("text")
      .data(root.descendants())
      .join("text")
      .style("fill-opacity", d => d.parent === root ? 0.6 : 0)
      .style("display", d => (d.parent === root && d.r > 8) ? "inline" : "none")
      .text((d: any) => d.data.name);

    function zoomTo(v: any) {
      const k = width / v[2];

      view = v;

      label.attr("transform", d => `translate(${(d.x - v[0]) * k},${(d.y - v[1]) * k})`);
      node.attr("transform", d => `translate(${(d.x - v[0]) * k},${(d.y - v[1]) * k})`);
      node.attr("r", d => d.r * k);
    }

    zoomTo([root.x, root.y, root.r * 2]);

  }

  onResize(): void {
    if(this.panel != null) {
      let w = Math.min(window.innerHeight-150, window.innerWidth);
      this.renderer.setStyle(this.panel.nativeElement, 'width', `${w}px`);
      this.renderer.setStyle(this.panel.nativeElement, 'height', `${w}px`);
      this.drawChart(this, this.data);
    }
  }
}
